import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import {
  Star,
  ThumbsUp,
  MessageSquare,
  Filter,
  Search,
  Calendar,
  BarChart2,
  ArrowUpRight,
  ArrowDownRight,
  Facebook,
  Globe,
  Send,
  AlertCircle,
  CheckCircle,
  Clock,
  X,
  TrendingUp,
  Zap
} from 'lucide-react';
import { format } from 'date-fns';

interface Review {
  id: string;
  platform: string;
  rating: number;
  content: string;
  author: string;
  date: string;
  status: 'responded' | 'pending' | 'flagged';
  response?: string;
}

const Reviews: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7d');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [response, setResponse] = useState('');

  const stats = [
    {
      label: "Overall Rating",
      value: "4.8",
      change: "+0.2",
      trend: "up",
      icon: <Star className="h-5 w-5 text-yellow-400" />
    },
    {
      label: "Total Reviews",
      value: "127",
      change: "+12",
      trend: "up",
      icon: <MessageSquare className="h-5 w-5 text-primary" />
    },
    {
      label: "Response Rate",
      value: "98%",
      change: "+5%",
      trend: "up",
      icon: <ThumbsUp className="h-5 w-5 text-success" />
    },
    {
      label: "Avg Response Time",
      value: "4h",
      change: "-1h",
      trend: "up",
      icon: <Clock className="h-5 w-5 text-warning" />
    }
  ];

  const platformStats = [
    {
      platform: "Google",
      icon: <Globe className="h-5 w-5" />,
      rating: 4.9,
      total: 86,
      responded: 84
    },
    {
      platform: "Facebook",
      icon: <Facebook className="h-5 w-5" />,
      rating: 4.7,
      total: 41,
      responded: 41
    },
    {
      platform: "Yelp",
      icon: <Star className="h-5 w-5" />,
      rating: 4.8,
      total: 32,
      responded: 31
    }
  ];

  // Rate Mojo data
  const rateMojoData = {
    score: 87,
    change: "+3",
    trend: "up",
    metrics: {
      responseSpeed: 92,
      sentimentScore: 85,
      engagementRate: 78,
      platformCoverage: 94
    }
  };

  const mockReviews: Review[] = [
    {
      id: '1',
      platform: 'Google',
      rating: 5,
      content: "Excellent financial advice! They helped me plan my retirement with confidence.",
      author: "John Smith",
      date: "2025-03-15T10:30:00Z",
      status: 'responded',
      response: "Thank you for your kind words, John! We're glad we could help with your retirement planning."
    },
    {
      id: '2',
      platform: 'Facebook',
      rating: 4,
      content: "Very professional team. Would recommend to others.",
      author: "Sarah Johnson",
      date: "2025-03-14T15:45:00Z",
      status: 'pending'
    },
    {
      id: '3',
      platform: 'Yelp',
      rating: 5,
      content: "Outstanding service and attention to detail.",
      author: "Michael Brown",
      date: "2025-03-13T09:20:00Z",
      status: 'flagged'
    }
  ];

  const handleResponseSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle response submission
    console.log('Response submitted:', response);
    setShowResponseModal(false);
    setResponse('');
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'google':
        return <Globe className="h-5 w-5 text-blue-500" />;
      case 'facebook':
        return <Facebook className="h-5 w-5 text-blue-600" />;
      case 'yelp':
        return <Star className="h-5 w-5 text-red-500" />;
      case 'rate mojo':
        return <Zap className="h-5 w-5 text-pink-500" />;
      default:
        return <Globe className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'responded':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-warning" />;
      case 'flagged':
        return <AlertCircle className="h-5 w-5 text-error" />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-gray-50 rounded-lg">
                {stat.icon}
              </div>
              <span className={`text-sm font-medium ${
                stat.trend === 'up' ? 'text-success' : 'text-error'
              }`}>
                {stat.change}
                {stat.trend === 'up' ? (
                  <ArrowUpRight className="h-4 w-4 inline ml-1" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 inline ml-1" />
                )}
              </span>
            </div>
            <h3 className="text-2xl font-bold mt-4">{stat.value}</h3>
            <p className="text-gray-600">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Platform Stats */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-6">Platform Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {platformStats.map((platform, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  {platform.icon}
                  <span className="ml-2 font-medium">{platform.platform}</span>
                </div>
                <span className="flex items-center text-yellow-500">
                  <Star className="h-4 w-4 fill-current" />
                  <span className="ml-1">{platform.rating}</span>
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total Reviews</span>
                  <span className="font-medium">{platform.total}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Response Rate</span>
                  <span className="font-medium">
                    {Math.round((platform.responded / platform.total) * 100)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
          
          {/* Rate Mojo Block */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Zap className="h-5 w-5 text-primary" />
                <span className="ml-2 font-medium">Rate Mojo</span>
              </div>
              <span className={`text-sm font-medium ${
                rateMojoData.trend === 'up' ? 'text-success' : 'text-error'
              }`}>
                {rateMojoData.change}
                {rateMojoData.trend === 'up' ? (
                  <ArrowUpRight className="h-4 w-4 inline ml-1" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 inline ml-1" />
                )}
              </span>
            </div>
            
            <div className="flex items-center justify-center mb-4">
              <div className="relative w-16 h-16 flex items-center justify-center">
                <svg className="w-full h-full" viewBox="0 0 100 100">
                  <circle 
                    cx="50" 
                    cy="50" 
                    r="45" 
                    fill="none" 
                    stroke="#e5e7eb" 
                    strokeWidth="10" 
                  />
                  <circle 
                    cx="50" 
                    cy="50" 
                    r="45" 
                    fill="none" 
                    stroke="#2A4B8C" 
                    strokeWidth="10" 
                    strokeDasharray="283" 
                    strokeDashoffset={283 - (283 * rateMojoData.score / 100)} 
                    transform="rotate(-90 50 50)" 
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">{rateMojoData.score}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              {Object.entries(rateMojoData.metrics).map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-gray-500">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                  <span className="font-medium">{value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center space-x-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search reviews..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <select
              className="border border-gray-300 rounded-md text-sm px-3 py-2"
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
            >
              <option value="all">All Platforms</option>
              <option value="google">Google</option>
              <option value="facebook">Facebook</option>
              <option value="yelp">Yelp</option>
              <option value="rate mojo">Rate Mojo</option>
            </select>
            <select
              className="border border-gray-300 rounded-md text-sm px-3 py-2"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Reviews</option>
              <option value="pending">Pending Response</option>
              <option value="responded">Responded</option>
              <option value="flagged">Flagged</option>
            </select>
            <select
              className="border border-gray-300 rounded-md text-sm px-3 py-2"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-6">Recent Reviews</h2>
          <div className="space-y-6">
            {mockReviews.map((review) => (
              <div
                key={review.id}
                className={`border rounded-lg p-4 ${
                  review.status === 'flagged' ? 'border-error/50 bg-error/5' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        {getPlatformIcon(review.platform)}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center">
                        <span className="font-medium">{review.author}</span>
                        <span className="mx-2">•</span>
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="mt-1 text-gray-600">{review.content}</p>
                      {review.response && (
                        <div className="mt-3 pl-4 border-l-2 border-gray-200">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Response: </span>
                            {review.response}
                          </p>
                        </div>
                      )}
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        {format(new Date(review.date), 'MMM d, yyyy')}
                        <span className="mx-2">•</span>
                        <div className="flex items-center">
                          {getStatusIcon(review.status)}
                          <span className="ml-1 capitalize">{review.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {review.status !== 'responded' && (
                      <button
                        onClick={() => {
                          setSelectedReview(review);
                          setShowResponseModal(true);
                        }}
                        className="btn btn-primary py-1 px-3"
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Respond
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Respond to Review</h3>
              <button
                onClick={() => setShowResponseModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center mb-2">
                <span className="font-medium">{selectedReview.author}</span>
                <span className="mx-2">•</span>
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${
                        i < selectedReview.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <p className="text-gray-600">{selectedReview.content}</p>
            </div>

            <form onSubmit={handleResponseSubmit}>
              <textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Write your response..."
                className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                required
              />
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  type="button"
                  onClick={() => setShowResponseModal(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <Send className="h-4 w-4 mr-2" />
                  Send Response
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;